import React from "react";
import {
    CommandBarButton,
    DefaultButton,
    DialogFooter,
    Modal,
    PrimaryButton,
    Stack,
    useDisclosure,
    useNotifications,
    useSprintQueryClient,
    WarningMessage
} from "sprint-common-web";

import { FileRecordQueryKey } from "../../../common/file-record-details/FileRecordQueryKey";
import { CloudFileStatus } from "../../common/types/CloudFileStatus";
import { CloudFileQueryKey } from "../common/CloudFileQueryKey";
import { useCloudFileDashboard } from "../common/context/useCloudFileDashboard";
import { useCloudFileDetails } from "../common/useCloudFileDetails";
import { FileRecordLookupItem } from "./FileRecordLookupItem";
import { FileRecordLookupListing } from "./FileRecordLookupListing";
import { useAssignFileRecordToCloudFile } from "./useAssignFileRecordToCloudFile";

export const FileRecordLookupButton = (): JSX.Element => {
    const { setLookupFileRecordId } = useCloudFileDashboard();
    const { success, error } = useNotifications();
    const [selectedItems, setSelectedItems] = React.useState<FileRecordLookupItem[]>([]);
    const { isOpen, onClose, onOpen } = useDisclosure();
    const sprintQueryClient = useSprintQueryClient();

    const { data: cloudFileDetails } = useCloudFileDetails();

    const { assignFileRecordToCloudFile } = useAssignFileRecordToCloudFile();

    const handleConfirmLookup = async () => {
        if (cloudFileDetails) {
            const cloudFileId = cloudFileDetails.cloudFileId;
            const fileRecordId = selectedItems[0].id;
            try {
                await assignFileRecordToCloudFile.mutateAsync({
                    cloudFileId,
                    fileRecordId
                });

                await sprintQueryClient.invalidateQueries({
                    queryKey: [CloudFileQueryKey.CloudFileDetails, cloudFileId]
                });
                await sprintQueryClient.invalidateQueries({
                    queryKey: [FileRecordQueryKey.FileRecordDetails, fileRecordId]
                });

                setLookupFileRecordId(fileRecordId);

                success("File record assigned successfully");
            } catch {
                error("Couldn't assign file record to cloud file");
            }

            onClose();
        }
    };

    return (
        <Stack>
            <CommandBarButton
                onClick={onOpen}
                text="Lookup file record"
                icon="PageHeaderEdit"
                testId="cloud-file-lookup-file-record-button"
                disabled={!canMakeLookup(cloudFileDetails?.status)}
            />
            <Modal
                isOpen={isOpen}
                headerText="Lookup file record"
                onDismiss={onClose}
                size="lg"
                styles={{ root: { height: "75vh" } }}
            >
                <FileRecordLookupListing
                    cloudFile={cloudFileDetails}
                    setSelectedItems={setSelectedItems}
                    selectedItems={selectedItems}
                />
                <Stack style={{ paddingTop: 20, paddingBottom: 10 }}>
                    <WarningMessage message="Data from the selected file record will overwrite the data saved in the form in the Associate section. If file record with multiple soundtracks is selected, only the first soundtrack will be used." />
                    <WarningMessage message="Associating a file record with a different record type will only copy fields common to both, such as languages and content. Other fields will be ignored." />
                    <WarningMessage message="The Waiting for Client Response status will be synced between the file record and cloud file." />
                </Stack>
                <DialogFooter styles={{ actions: { lineHeight: 0, marginTop: 0 } }}>
                    <Stack horizontal={true} horizontalAlign="end" tokens={{ childrenGap: 10, padding: 10 }}>
                        <DefaultButton text="Cancel" onClick={onClose} />
                        <PrimaryButton
                            disabled={!selectedItems.length}
                            onClick={handleConfirmLookup}
                            text={"Confirm"}
                            type="loading"
                            testId="confirm"
                        />
                    </Stack>
                </DialogFooter>
            </Modal>
        </Stack>
    );
};

const canMakeLookup = (cloudFileStatus: CloudFileStatus | undefined): boolean => {
    return cloudFileStatus === CloudFileStatus.InQc || cloudFileStatus === CloudFileStatus.WaitingForClientResponse;
};
